// External
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import TextAreaInput from "UIComponents/TextAreaInput";
import { MicrophoneOutlined, StarsTwoFilled } from "@toddle-edu/ds-icons";
import { ThreeDots } from "react-loader-spinner";
import { connect } from "react-redux";

// Internal
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import { useCallbackRef } from "Utils";
import { getSpeechToTextTranscript } from "modules/Services";
import { colors } from "Constants";
import { Sentry } from "services/sentry";

// Relative
import classes from "./AiQuestionGeneratorCustomInstruction.scss";
import { CONTEXT_FIELDS } from "../AiQuestionGeneratorConstants";
import DynamicPromptInput from "./DynamicPromptInput";
import {
  getOrderedPrompts,
  isUsingPresetPrompt,
} from "./AiQuestionGeneratorCustomInstructionUtils";

const { CF_AI_INSTRUCTIONS } = CONTEXT_FIELDS;

const styles = {
  voiceContainerStyle: {
    position: "absolute",
    zIndex: 999,
    width: "max-content",
    right: "12px",
  },
  textAreaStyles: {
    padding: "0px 44px 0px 0px",
    border: "none",
    minHeight: "100px",
  },
  alertContainerStyle: {
    top: "8px",
    insetInline: "50% auto",
    transform: "translateX(-50%)",
  },
};

// * Voice to text converting loader
const ConvertingToTextLoader = ({ t, isAudioLoading }) => {
  if (!isAudioLoading) return null;
  return (
    <div className={classes.convertingToTextLoader}>
      <ThreeDots
        height="16"
        width="24"
        radius="3"
        color={colors.surfaceActive}
        ariaLabel="three-dots-loading"
        visible={true}
      />
      <div className="text-body">{t("ai:converting_to_text")}</div>
    </div>
  );
};

// * Preset Prompts List
const PresetPrompts = ({ instruction, presetPrompts, onClick }) => {
  const prompts = useMemo(() => {
    return getOrderedPrompts(instruction, presetPrompts);
  }, [presetPrompts, instruction]);

  const labelClass = classNames(classes.presetPromptItemLabel, "text-label-s");

  return (
    <div className={classes.presetPromptContainer}>
      {_.map(_.take(prompts, 3), (prompt, index) => {
        const displayText = _.get(prompt, "displayText", "");
        if (_.isEmpty(displayText)) return null;

        return (
          <Tooltip
            dsVersion="2.0"
            showArrow={true}
            tooltip={displayText}
            key={prompt?.id}
          >
            <Button
              size="small"
              dsVersion="2.0"
              type="outlined"
              variant="neutral"
              key={prompt?.id}
              className={classes.presetPromptItem}
              onClick={() => onClick(prompt)}
              testId={"worksheet-ai-generator-preset-prompt " + index}
              icon={
                <StarsTwoFilled
                  size="xxx-small"
                  variant="success"
                  className={classes.presetPromptItemIcon}
                />
              }
            >
              <div className={labelClass}>{displayText}</div>
            </Button>
          </Tooltip>
        );
      })}
    </div>
  );
};

/**
 *
 * @param {*}
 * @returns Custom Instruction Component
 */
const AiQuestionGeneratorCustomInstruction = ({
  t,
  onChange,
  instruction,
  presetPrompts = [],
  aiConversationId,
  getSpeechToTextTranscript,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [activeInput, setActiveInput] = useState("ADDITIONAL_INSTRUCTIONS_ID");

  const [audioRecorderNodeRef, setAudioRecorderNodeRef] = useCallbackRef();
  const isSpeechToTextLoading = isRecording || isAudioLoading;

  const isPresetPrompt = useMemo(() => {
    return isUsingPresetPrompt(instruction, presetPrompts);
  }, [presetPrompts, instruction]);

  useEffect(() => {
    if (isPresetPrompt) {
      setSelectedPrompt(_.get(instruction, "selectedPrompt", null));
    }
  }, [isPresetPrompt, instruction]);

  // * Converting audio file into transcript and updating the instruction
  const handleTranscription = async attachment => {
    try {
      const response = await getSpeechToTextTranscript({
        entityId: aiConversationId,
        entityType: "AI_CONVERSATION",
        blobAttachment: attachment,
      });
      const transcript = _.get(response, "transcript", "");

      if (!selectedPrompt) {
        // * For plain custom instruction
        const starter = _.get(instruction, "data", "");
        const instructionWithTranscript = `${starter} ${transcript}`;
        onChange(CF_AI_INSTRUCTIONS, {
          selectedPrompt: null,
          data: instructionWithTranscript,
        });
      } else {
        // * For Selected preset prompt
        if (activeInput) {
          // * If active input then adding transcript in it
          const updatedData = _.map(_.get(instruction, "data", []), item => {
            if (_.get(item, "key", "") === activeInput) {
              return {
                ...item,
                data: `${_.get(item, "data", "")} ${transcript}`,
              };
            }
            return item;
          });
          const _selectedPrompt = selectedPrompt;
          setSelectedPrompt(null);
          onChange(CF_AI_INSTRUCTIONS, {
            selectedPrompt: _selectedPrompt,
            data: updatedData,
          });
          setSelectedPrompt(_selectedPrompt);
        }
      }
    } catch (error) {
      Sentry.captureMessage(`AiConversationSpeechToText: ${error.message}`, {
        extra: {
          aiConversationId,
          attachment,
        },
      });
    } finally {
      setIsAudioLoading(false);
      setIsRecording(false);
    }
  };

  // * On selecting preset prompts
  const handlePromptSelection = prompt => {
    const displayText = _.get(prompt, "displayText", "");
    const promptText = _.get(prompt, "metadata.presetPrompt", displayText);
    setSelectedPrompt(promptText);
    onChange(CONTEXT_FIELDS.CF_AI_INSTRUCTIONS, {
      selectedPrompt: promptText,
      data: null,
    });
  };

  return (
    <div className={classes.contentWrapper}>
      <div className="text-label">
        {t("worksheet:custom_instructions_toddle_ai")}
      </div>

      <div
        ref={setAudioRecorderNodeRef}
        className={classNames({
          [classes.customInstructionInputContainer]: !isRecording,
          [classes.convertingToText]: isAudioLoading,
        })}
      >
        {/* Audio to transcript Loader */}
        <ConvertingToTextLoader t={t} isAudioLoading={isAudioLoading} />

        {/* Speech to Text with Mic icon */}
        <AudioRecorderSelector
          showValue={false}
          version="visualiser"
          showProgress={false}
          disabled={isAudioLoading}
          parentId={aiConversationId}
          parentType="AI_CONVERSATION"
          isSpeechToTextVisualiser={true}
          container={audioRecorderNodeRef}
          header={t("common:add_voice_message")}
          setChatbarVisible={value => setIsRecording(!value)}
          onBlobAttachmentRecieved={handleTranscription}
          onFileUpload={() => {
            setIsAudioLoading(true);
            setIsRecording(false);
          }}
          buttonComponent={
            <div>
              {!isSpeechToTextLoading && (
                <IconButton
                  dsVersion="2.0"
                  variant="neutral"
                  type="outlined"
                  size="small"
                  icon={<MicrophoneOutlined />}
                  disabled={isRecording || isAudioLoading}
                  testId="worksheet-ai-generator-custom-instruction-audio-recorder"
                />
              )}
            </div>
          }
          containerStyle={
            isSpeechToTextLoading ? {} : styles.voiceContainerStyle
          }
          alertContainerStyle={styles.alertContainerStyle}
        />

        {/* Preset prompt Dynamic prompt Input */}
        {isPresetPrompt && !isRecording && !isAudioLoading && (
          <DynamicPromptInput
            t={t}
            onChange={onChange}
            instruction={instruction}
            selectedPrompt={selectedPrompt}
            setActiveInput={setActiveInput}
            setSelectedPrompt={setSelectedPrompt}
          />
        )}

        {/* Custom instruction plain input and preset prompts */}
        {!isSpeechToTextLoading && (
          <>
            {!isPresetPrompt && (
              <TextAreaInput
                maxRows={4}
                dsVersion="2.0"
                value={_.get(instruction, "data", "")}
                className="text-body"
                textAreaStyles={styles.textAreaStyles}
                autoFocus={true}
                placeholder={t("worksheet:question_description_guidelines")}
                updateInputField={value =>
                  onChange(CF_AI_INSTRUCTIONS, {
                    selectedPrompt: null,
                    data: value,
                  })
                }
              />
            )}

            <PresetPrompts
              instruction={instruction}
              presetPrompts={presetPrompts}
              onClick={handlePromptSelection}
            />
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToDispatch = {
  getSpeechToTextTranscript,
};

export default connect(
  null,
  mapStateToDispatch
)(AiQuestionGeneratorCustomInstruction);
